//
// Current logged in user information
//
// auth: PropTypes.shape({
//   info: PropTypes.shape({
//     username: PropTypes.string,
//     fname: PropTypes.string,
//     lname: PropTypes.string,
//     fake: PropTypes.number,
//     staff: PropTypes.number,
//     usid: PropTypes.string,
//     rlid: PropTypes.string,
//     cuid: PropTypes.string,
//     grpGpid: PropTypes.string,
//     grpLbl: PropTypes.string,
//     gcatCode: PropTypes.string,
//     privlg: PropTypes.arrayOf(PropTypes.string),
//     iAmRescue: PropTypes.bool.isRequired,
//   }),
//   server: PropTypes.shape({
//     reqPending: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
//     register: PropTypes.func,
//     login: PropTypes.func,
//     logout: PropTypes.func,
//     getInfo: PropTypes.func,
//     ready: PropTypes.bool,
//   }),
//   superagentGet: PropTypes.func.isRequired,
//   superagentPost: PropTypes.func.isRequired,
//   superagentGetAsync: PropTypes.func.isRequired,
//   superagentPostAsync: PropTypes.func.isRequired,
// }).isRequired,
//

import PropTypes from 'prop-types';
import {
  useEffect,
  useCallback,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import superagent from 'superagent';

import {
  config,
  displayJson,
  logDev,
  logError,
  rescueCategories,
  registerLoader,
  completeLoader,
  piidIsHome,
  requestSuspendedError,
  urlPathHasMissingValue,
  payloadWithMsAgo,
} from '../../utils';
import useStats from '../stats/useStats';

import AuthContext from './AuthContext';

// Register auth loader at module load time (before React renders)
const authLoaderId = registerLoader('AuthProvider.status');

// Session storage, so a reload keeps the pending login; never local storage, which other tabs read
const loginPendingStorageKey = 'px_login_pending';

const readStoredLoginPending = () => {
  try {
    return JSON.parse(sessionStorage.getItem(loginPendingStorageKey)) ?? undefined;
  } catch {
    return undefined;
  }
};

const storeLoginPending = (loginPending) => {
  if (loginPending) sessionStorage.setItem(loginPendingStorageKey, JSON.stringify(loginPending));
  else sessionStorage.removeItem(loginPendingStorageKey);
};

const initialState = {
  serverStatus: null,
  serverError: null,
  userInfo: undefined,
  reqPending: 'Initialisation...',
  partnersMap: undefined,
  loginPending: undefined,
  loginPendingExpired: false,
  recoveryCodeUsed: false,
};

const stateReducer = (state, action) => {
  switch (action.type) {
    case 'setUserInfo': {
      const {
        userInfo,
        serverStatus,
        partnersMapRef,
        keepUserInfo,
      } = action;
      let newPartnersMap;

      // An answer that started before the current login or logout carries the
      // server patterns we want and a user we have already moved past
      if (keepUserInfo) {
        return (serverStatus)
          ? { ...state, serverStatus }
          : state;
      }

      if (userInfo) {
        const iAmRescue = (!!userInfo.gcatCode)
                        && rescueCategories.includes(userInfo.gcatCode);

        userInfo.iAmRescue = iAmRescue;

        if (userInfo.partners) {
          userInfo.partners.forEach((thisPartner) => {
            const { piid, ...restPartner } = thisPartner;
            if (!newPartnersMap) newPartnersMap = {};

            newPartnersMap[piid] = restPartner;
          });
        }
      }

      const newState = {
        ...state,
        userInfo,
        partnersMap: newPartnersMap,
      };

      if (serverStatus) newState.serverStatus = serverStatus;

      // We keep two copies of this, a ref based one to make sure there is no
      // cyclic dependencies inside this provider:
      partnersMapRef.current = newPartnersMap;

      return newState;
    }

    case 'setReqPending': {
      const { reqPending } = action;

      return {
        ...state,
        reqPending,
      };
    }

    case 'setServerError': {
      const { serverError } = action;

      return {
        ...state,
        serverError,
      };
    }

    case 'setLoginPending':
      return {
        ...state,
        loginPending: action.loginPending,
        loginPendingExpired: Boolean(action.expired),
      };

    case 'setRecoveryCodeUsed':
      return {
        ...state,
        recoveryCodeUsed: action.recoveryCodeUsed,
      };

    default:
      logError(
        `AuthProvider - ignore action=${displayJson(action)}`,
        'AuthProvider.reducer.unknownAction',
        { actionType: action?.type },
      );
      return state;
  }
};

const getHomeJwt = () => (localStorage.getItem('back_home_jwt'));

const removeAllJwt = () => {
  Object.keys(localStorage)
    .filter((key) => (key.endsWith('_jwt') || key.endsWith('_url')))
    .forEach((key) => localStorage.removeItem(key));

  localStorage.removeItem('back_home_jwt');
};

function AuthProvider(props) {
  const {
    children,
    invite,
  } = props;
  const { stats } = useStats();
  const {
    startUrl,
    endUrl,
  } = stats;
  const [state, dispatch] = useReducer(
    stateReducer,
    initialState,
    (initial) => ({
      ...initial,
      loginPending: readStoredLoginPending(),
    }),
  );
  const jwtReqStatusRef = useRef({});
  const partnersMapRef = useRef({});
  const isUnloadingRef = useRef(false);
  // Closed as soon as logout starts: every later call is a 403 counting towards an IP ban.
  const loggingOutRef = useRef(false);
  // Bumped when a login or logout starts and again once its answer is in: a status
  // call started at any point before then asked about a session that no longer stands.
  const authEpochRef = useRef(0);
  // Base every settings write merges into while a batch is in flight: the render
  // closure still holds the copy from before those writes.
  const pendingUserSettingsRef = useRef(null);
  const userSettingsWritesRef = useRef(0);
  const userSettingsChainRef = useRef(Promise.resolve());

  const {
    reqPending,
    serverError,
    serverStatus,
    userInfo,
  } = state;

  //
  // Fetch a partner JWT from home server
  // Stores JWT in localStorage
  //
  const fetchPartnerJwt = useCallback(
    async (piid, rlid) => {
      if (loggingOutRef.current) return null;

      const homeJwt = getHomeJwt();
      const url = config.town.back.urlPrefix + config.town.back.auth.partner;

      logDev(`fetchPartnerJwt piid=${piid} rlid=${rlid}`);

      let res;
      let error;

      try {
        const request = superagent
          .post(url)
          .set('Content-Type', 'application/json')
          .withCredentials();

        if (homeJwt) request.auth(homeJwt, { type: 'bearer' });

        res = await request.send({ piid });
      } catch (err) {
        error = err;
        logError(
          `fetchPartnerJwt error piid=${piid}, rlid=${rlid}, error=${displayJson(err)}`,
          'AuthProvider.fetchPartnerJwt.error',
          {
            piid,
            err: err?.message,
            ...(err?.status && { status: err.status }),
          },
        );
      }

      // logDev(`fetchPartnerJwt error=${error} res.body=${displayJson(res.body)}`);

      if ((!error) && res.body?.rslt?.data?.length) {
        const { jwt } = res.body.rslt.data[0];

        if (jwt) {
          localStorage.setItem(`back_${piid}_${rlid}_jwt`, jwt);

          return jwt;
        }
      }

      logError(
        `fetchPartnerJwt failed piid=${piid}, rlid=${rlid}`,
        'AuthProvider.fetchPartnerJwt.noJwt',
        {
          piid,
          hasResponseData: !!res?.body?.rslt?.data?.length,
        },
      );
      return null;
    },
    [],
  );

  //
  // Get JWT and base URL for a customer server
  // If a partner piid and rlid are provided: partner server (fetches JWT if needed)
  // If not provided, or piid is the "home" alias: home server
  //
  const getCstmrJwtAndUrl = useCallback(
    async (piid, rlid) => {
      if ((!piidIsHome(piid)) && rlid) {
        // Partner site
        //
        const jwt = localStorage.getItem(`back_${piid}_${rlid}_jwt`);
        let urlBase = partnersMapRef.current?.[piid]?.backUrl;

        if (!urlBase) {
          logError(
            `getCstmrJwtAndUrl no backUrl for piid=${piid}`,
            'AuthProvider.getCstmrJwtAndUrl.noBackUrl',
            {
              piid,
              partnersMapKeys: partnersMapRef.current && Object.keys(partnersMapRef.current),
            },
          );
          logDev(`partnersMapRef.current=${displayJson(partnersMapRef.current)}`);
          return [null, null];
        }

        urlBase += '/';

        if (jwt) return [jwt, urlBase];

        // No JWT yet - fetch it from home server
        const newJwt = await fetchPartnerJwt(piid, rlid);
        return [newJwt, urlBase];
      }

      // Home site
      const urlBase = config.town.back.urlPrefix;
      const jwt = getHomeJwt();

      return [jwt, urlBase];
    },
    [fetchPartnerJwt],
  );

  const refreshHomeJwt = useCallback(
    async (type = 'back') => {
      if (loggingOutRef.current) return;
      //
      // Refresh home server JWT
      //
      const key = `${type}_home_jwt`;

      if (jwtReqStatusRef.current[key] !== 'pending') {
        const jwt = getHomeJwt();
        const url = config.town.back.urlPrefix
                  + config.town.back.auth.refresh;

        jwtReqStatusRef.current[key] = 'pending';

        let res;
        let error;

        try {
          const request = superagent
            .get(url)
            .withCredentials();

          if (jwt) request.auth(jwt, { type: 'bearer' });

          res = await request.send();
        } catch (err) {
          error = err;
          logError(
            `refreshHomeJwt error url=${url}, error=${displayJson(err)}`,
            'AuthProvider.refreshHomeJwt.error',
            {
              type,
              err: err?.message,
              ...(err?.status && { status: err.status }),
            },
          );
        }

        if (!error) {
          const newJwt = res.body.rslt.data[0]?.jwt;

          if (newJwt) {
            jwtReqStatusRef.current[key] = 'ready';
            localStorage.setItem('back_home_jwt', newJwt);
          } else {
            logError(
              `refreshHomeJwt error url=${url}, no JWT in response`,
              'AuthProvider.refreshHomeJwt.noJwtInResponse',
              {
                type,
                resStatus: res?.status,
                hasResponseData: !!res?.body?.rslt?.data?.length,
              },
            );
            error = true;
          }
        }

        if (error) {
          jwtReqStatusRef.current[key] = 'error';
        }
      }
    },
    [],
  );

  const refreshJwt = useCallback(
    async (piid, rlid, type = 'back') => {
      logDev(`refreshJwt piid=${piid} rlid=${rlid} type=${type}`);

      if ((!piidIsHome(piid)) && rlid) {
        //
        // Refresh a partner JWT - same as initial fetch
        //
        await fetchPartnerJwt(piid, rlid);
      } else {
        //
        // Refresh home server JWT
        //
        await refreshHomeJwt();
      }
    },
    [fetchPartnerJwt, refreshHomeJwt],
  );

  const getStatus = useCallback(
    async ({ sessionEnded = false } = {}) => {
      if (!invite) {
        const epoch = authEpochRef.current;

        dispatch({
          type: 'setReqPending',
          reqPending: 'Initialisation...',
        });

        // finally: a throw past the request must not leave the auth loader waiting forever
        try {
          const url = config.town.back.urlPrefix
                    + config.town.back.auth.status;

          const jwt = getHomeJwt();

          const statCnt = startUrl(url);
          let res = [];
          let error;
          try {
            const request = superagent
              .get(url)
              .withCredentials();

            if (jwt) {
              logDev('getStatus WITH jwt');
              request.auth(jwt, { type: 'bearer' });
            } else {
              // No stored JWT at boot is just the logged-out state; real session loss surfaces via refreshHomeJwt.noJwtInResponse / getStatus.noToken.
              logDev('getStatus NO jwt');
            }

            res = await request.send();
          } catch (err) {
            if (err.status === 403) {
              dispatch({
                type: 'setUserInfo',
                userInfo: null,
                partnersMapRef,
                keepUserInfo: (epoch !== authEpochRef.current),
              });
            } else if (!err.status) {
              // Network error (server unreachable)
              dispatch({
                type: 'setServerError',
                serverError: 'unreachable',
              });
            }
            error = err;
            logError(
              `getStatus error url=${url}, error=${displayJson(err)}`,
              'AuthProvider.getStatus.error',
              {
                err: err?.message,
                ...(err?.status && { status: err.status }),
              },
            );
          }
          endUrl(url, statCnt);

          // Check for stale JWT header
          let staleJwt;
          let missingJwt;
          if (res?.headers?.['px-stale-jwt']) {
            logError(
              'getStatus JWT needs refresh!',
              // 'AuthProvider.getStatus.staleJwt',
            );
            staleJwt = true;
          }
          if (res?.headers?.['px-no-jwt']) {
            const expiredJwt = !!res?.headers?.['px-expired-jwt'];

            // A logged-out visitor gets px-no-jwt on every call, and a session that reached
            // its end is expected; only a JWT the server refused for another reason is news
            logError(
              'getStatus No PX token',
              (jwt && (!expiredJwt) && (!sessionEnded)) ? 'AuthProvider.getStatus.noToken' : undefined,
              {
                hasStaleHeader: !!res?.headers?.['px-stale-jwt'],
                expiredJwt,
              },
            );
            // Remove from sstorage a refused token, avoid failing further API calls
            if (jwt && (getHomeJwt() === jwt)) removeAllJwt();
            missingJwt = true;
          }

          if ((!error) && (res.body?.rslt?.data?.length)) {
            const {
              regex,
              userInfo: newUserInfo,
            } = res.body.rslt.data[0];

            const newStatus = {
              regex: {
                names: new RegExp(regex.names),
                password: new RegExp(regex.pxPassword),
                username: new RegExp(regex.pxUsername),
                mail: (regex.mail ?? []).map((pattern) => new RegExp(pattern)),
                phone: new RegExp(regex.phone ?? ''),
                uid: new RegExp(regex.uid ?? ''),
              },
            };

            // !!!!!!!!!!!!!!!!!!!!!!!!!
            // Temp: transition from cookies to JWT
            // !!!!!!!!!!!!!!!!!!!!!!!!!
            if ((staleJwt || missingJwt) && newUserInfo?.isAuthenticated) {
              // Use void to make it clear we do not want to wait this async function:
              void refreshHomeJwt();
            }

            // Clear any previous server error
            dispatch({
              type: 'setServerError',
              serverError: null,
            });

            // Separate status, so it triggers a refresh...
            dispatch({
              type: 'setUserInfo',
              userInfo: newUserInfo,
              serverStatus: newStatus,
              partnersMapRef,
              keepUserInfo: (epoch !== authEpochRef.current),
            });
          }
        } finally {
          dispatch({
            type: 'setReqPending',
            reqPending: false,
          });
        }
      }
    },
    [invite, startUrl, endUrl, refreshHomeJwt],
  );

  useEffect(
    () => {
      logDev(`AuthProvider userInfo=${displayJson(userInfo)} partnersMap=${
        displayJson(partnersMapRef.current)}`);
    },
    [userInfo],
  );

  // Compute auth status for loading gate coordination
  // - 'init': still checking auth status
  // - 'loggedIn': user is authenticated
  // - 'loggedOut': user is not authenticated
  // Note: userInfo can be an object with isAuthenticated:false for logged-out users
  const authStatus = useMemo(
    () => {
      if (reqPending) return 'init';

      if (userInfo && (userInfo.isAuthenticated !== false)) return 'loggedIn';

      return 'loggedOut';
    },
    [reqPending, userInfo],
  );

  // Complete the auth loader when auth status is resolved (no longer pending)
  useEffect(
    () => {
      if (!reqPending) {
        completeLoader(authLoaderId);
      }
    },
    [reqPending],
  );

  const login = useCallback(
    async (username, password) => {
      authEpochRef.current += 1;
      loggingOutRef.current = false;

      // A password entered again starts over: the server replaces the role's pending login
      storeLoginPending(undefined);
      dispatch({
        type: 'setLoginPending',
        loginPending: undefined,
      });

      if (!invite) {
        dispatch({
          type: 'setReqPending',
          reqPending: `Connexion du profile ${username}...`,
        });
      }

      const currentJwt = getHomeJwt();

      // In case we somehow change account, erase all JWT's:
      removeAllJwt();

      const url = (invite)
        ? config.town.demo.urlPrefix + config.town.demo.auth.login
        : config.town.back.urlPrefix + config.town.back.auth.login;
      const statCnt = startUrl(url);
      let res = [];
      let error;
      const payload = {
        username,
      };
      if (invite) payload.inviteCode = password;
      else payload.password = password;

      try {
        const request = superagent
          .post(url)
          .set('Content-Type', 'application/json') // headers
          .withCredentials();

        if (currentJwt) request.auth(currentJwt, { type: 'bearer' });

        res = await request.send(payloadWithMsAgo(payload));
      } catch (err) {
        error = err;
      }
      endUrl(url, statCnt);
      authEpochRef.current += 1;

      dispatch({
        type: 'setReqPending',
        reqPending: false,
      });

      if ((!error) && (res.body.rslt.data[0]?.loginPendingId)) {
        const { loginPendingId, steps, methods } = res.body.rslt.data[0];
        const loginPending = {
          loginPendingId, steps, methods, username,
        };

        storeLoginPending(loginPending);
        dispatch({
          type: 'setLoginPending',
          loginPending,
        });

        return true;
      }

      if ((!error) && (res.body.rslt.data.length)) {
        dispatch({
          type: 'setUserInfo',
          userInfo: res.body.rslt.data[0].userInfo,
          partnersMapRef,
        });

        const { jwt: newJwt } = res.body.rslt.data[0];

        if (newJwt && (!invite)) {
          localStorage.setItem('back_home_jwt', newJwt);
        }

        return true;
      }

      logError(
        'login error, cleanup user info',
        // 'AuthProvider.login.failed',
      );
      dispatch({
        type: 'setUserInfo',
        userInfo: null,
        partnersMapRef,
      });

      return false;
    },
    [invite, startUrl, endUrl],
  );

  const loginStepCancel = useCallback(
    (expired) => {
      storeLoginPending(undefined);
      dispatch({
        type: 'setLoginPending',
        loginPending: undefined,
        expired: (expired === true),
      });
    },
    [],
  );

  const loginStep = useCallback(
    async (stepUrl, body = {}) => {
      const loginPending = readStoredLoginPending();
      if (!loginPending) {
        loginStepCancel(true);
        return { loginPendingExpired: true };
      }

      authEpochRef.current += 1;
      loggingOutRef.current = false;

      const url = config.town.back.urlPrefix + stepUrl;
      const statCnt = startUrl(url);
      let res;
      let error;

      try {
        res = await superagent
          .post(url)
          .set('Content-Type', 'application/json') // headers
          .withCredentials()
          .send({
            loginPendingId: loginPending.loginPendingId,
            ...body,
          });
      } catch (err) {
        error = err;
      }
      endUrl(url, statCnt);
      authEpochRef.current += 1;

      // Outside DEV nginx strips a refusal's CORS headers, so every failure reads as a refused code
      if (error) {
        if (error.status && (![401, 403].includes(error.status))) {
          logError(
            `loginStep error url=${url}, error=${displayJson(error)}`,
            'AuthProvider.loginStep.error',
            {
              err: error?.message,
              status: error.status,
            },
          );
        }

        return { badCode: true };
      }

      const answer = res.body.rslt.data[0] ?? {};

      if (answer.jwt) {
        loginStepCancel();
        localStorage.setItem('back_home_jwt', answer.jwt);
        dispatch({
          type: 'setUserInfo',
          userInfo: answer.userInfo,
          partnersMapRef,
        });
        if (answer.recoveryCodeUsed) {
          dispatch({
            type: 'setRecoveryCodeUsed',
            recoveryCodeUsed: true,
          });
        }

        return { done: true };
      }

      if (answer.loginPendingExpired) loginStepCancel(true);

      if (answer.steps) {
        const nextLoginPending = {
          ...loginPending,
          steps: answer.steps,
          ...(answer.methods ? { methods: answer.methods } : {}),
        };

        storeLoginPending(nextLoginPending);
        dispatch({
          type: 'setLoginPending',
          loginPending: nextLoginPending,
        });
      }

      return answer;
    },
    [startUrl, endUrl, loginStepCancel],
  );

  const dismissRecoveryCodeUsed = useCallback(
    () => {
      dispatch({
        type: 'setRecoveryCodeUsed',
        recoveryCodeUsed: false,
      });
    },
    [],
  );

  const logout = useCallback(
    async () => {
      authEpochRef.current += 1;
      loggingOutRef.current = true;

      dispatch({
        type: 'setReqPending',
        reqPending: 'Déconnexion...',
      });

      const jwt = getHomeJwt();
      // Cleared before the server revokes the session, so other tabs close their gate first
      removeAllJwt();

      const url = (invite)
        ? config.town.demo.urlPrefix + config.town.demo.auth.logout
        : config.town.back.urlPrefix + config.town.back.auth.logout;
      const statCnt = startUrl(url);
      try {
        const request = superagent
          .post(url)
          .set('Content-Type', 'application/json') // headers
          .withCredentials();

        if (jwt) request.auth(jwt, { type: 'bearer' });

        await request.send({});
      } catch (err) {
        logError(
          `logout error url=${url}, error=${displayJson(err)}`,
          'AuthProvider.logout.error',
          {
            err: err?.message,
            ...(err?.status && { status: err.status }),
          },
        );
      }
      endUrl(url, statCnt);
      authEpochRef.current += 1;

      dispatch({
        type: 'setUserInfo',
        userInfo: null,
        partnersMapRef,
      });

      dispatch({
        type: 'setReqPending',
        reqPending: false,
      });
    },
    [invite, startUrl, endUrl],
  );

  // A Paxity admin lands from www-admin-anon with a one-time code: the session
  // this browser may hold is closed first, then the code is traded for the
  // admin session exactly as a password login would be
  const loginAdmin = useCallback(
    async (anonLoginReqId) => {
      authEpochRef.current += 1;
      if (getHomeJwt()) await logout();
      loggingOutRef.current = false;

      dispatch({
        type: 'setReqPending',
        reqPending: 'Connexion du support Paxity...',
      });

      const url = config.town.back.urlPrefix + config.town.back.auth.loginAdmin;
      const statCnt = startUrl(url);
      let res;
      let error;

      try {
        res = await superagent
          .post(url)
          .set('Content-Type', 'application/json') // headers
          .withCredentials()
          .send({ anonLoginReqId });
      } catch (err) {
        error = err;
      }
      endUrl(url, statCnt);
      authEpochRef.current += 1;

      dispatch({
        type: 'setReqPending',
        reqPending: false,
      });

      if ((!error) && (res.body.rslt.data.length)) {
        dispatch({
          type: 'setUserInfo',
          userInfo: res.body.rslt.data[0].userInfo,
          partnersMapRef,
        });

        localStorage.setItem('back_home_jwt', res.body.rslt.data[0].jwt);

        return true;
      }

      logError(
        `loginAdmin error url=${url}, error=${displayJson(error)}`,
        'AuthProvider.loginAdmin.error',
        {
          err: error?.message,
          ...(error?.status && { status: error.status }),
        },
      );
      dispatch({
        type: 'setUserInfo',
        userInfo: null,
        partnersMapRef,
      });

      return false;
    },
    [logout, startUrl, endUrl],
  );

  const getInfo = useCallback(
    async () => {
      if (loggingOutRef.current) return;

      dispatch({
        type: 'setReqPending',
        reqPending: 'Téléchargement des données de l\'utilisateur...',
      });

      // finally: a throw past the request must not leave the auth loader waiting forever
      try {
        const jwt = getHomeJwt();

        const url = (invite)
          ? config.town.demo.urlPrefix + config.town.demo.auth.getInfo
          : config.town.back.urlPrefix + config.town.back.auth.getInfo;

        const statCnt = startUrl(url);
        let res = [];
        let error;

        try {
          const request = superagent
            .get(url)
            .withCredentials();

          if (jwt) request.auth(jwt, { type: 'bearer' });

          res = await request.send();
        } catch (err) {
          if (err.status === 403) {
            dispatch({
              type: 'setUserInfo',
              userInfo: null,
              partnersMapRef,
            });
          }
          error = err;
          logError(
            `getInfo error url=${url}, error=${
              error.statusText}, status=${error.status}`,
            'AuthProvider.getInfo.error',
            {
              err: error?.message,
              ...(error?.status && { status: error.status }),
            },
          );
        }
        endUrl(url, statCnt);

        if ((!error) && (res.body?.rslt?.data?.length)) {
          dispatch({
            type: 'setUserInfo',
            userInfo: res.body.rslt.data[0],
            partnersMapRef,
          });
        } else {
          dispatch({
            type: 'setUserInfo',
            userInfo: null,
            partnersMapRef,
          });
        }
      } finally {
        dispatch({
          type: 'setReqPending',
          reqPending: false,
        });
      }
    },
    [invite, startUrl, endUrl],
  );

  useEffect(
    () => {
      if (invite) {
        // Use void to make it clear we do not want to wait this async function:
        void getInfo();
      } else {
        // Use void to make it clear we do not want to wait this async function:
        void getStatus();
      }
    },
    [getInfo, getStatus, invite],
  );

  // Storage is shared by every tab: a login or logout in another one leaves this
  // tab holding somebody else's token while still showing the previous identity
  useEffect(
    () => {
      if (invite) return undefined;

      const onStorage = (event) => {
        if (event.key !== 'back_home_jwt') return;

        authEpochRef.current += 1;

        // Another tab logged out: any call from here would be a 403 counting towards an IP ban
        if (event.newValue === null) {
          loggingOutRef.current = true;
          dispatch({
            type: 'setUserInfo',
            userInfo: null,
            partnersMapRef,
          });
          return;
        }

        loggingOutRef.current = false;
        // Use void to make it clear we do not want to wait this async function:
        void getStatus();
      };

      window.addEventListener('storage', onStorage);
      return () => window.removeEventListener('storage', onStorage);
    },
    [getStatus, invite],
  );

  // The websocket reports the session ended on the server (logout from another device,
  // session revoked): the gate closes synchronously, before the fallback poll starts
  const recheckEndedSession = useCallback(
    async () => {
      if (loggingOutRef.current || (!getHomeJwt())) return;

      loggingOutRef.current = true;
      try {
        await getStatus({ sessionEnded: true });
      } finally {
        if (getHomeJwt()) loggingOutRef.current = false;
      }
    },
    [getStatus],
  );

  // Track page unload so in-flight fetches that get aborted by the browser
  // don't bubble up as errors to the Slack reporter
  useEffect(
    () => {
      const onHide = () => {
        isUnloadingRef.current = true;
      };
      const onShow = () => {
        isUnloadingRef.current = false;
      };
      window.addEventListener('pagehide', onHide);
      window.addEventListener('pageshow', onShow);
      return () => {
        window.removeEventListener('pagehide', onHide);
        window.removeEventListener('pageshow', onShow);
      };
    },
    [],
  );

  const superagentGet = useCallback(
    async (url, set, callback) => {
      if (loggingOutRef.current) {
        callback(requestSuspendedError(url));
        return;
      }

      const statCnt = startUrl(url);

      const jwt = getHomeJwt();

      try {
        const request = superagent
          .get(url)
          .set(...set)
          .withCredentials();

        if (jwt) request.auth(jwt, { type: 'bearer' });

        request.end((err, res) => {
          if (err) {
            if (err.status === 403) {
              // Use void to make it clear we do not want to wait this async function:
              void getInfo();
            }
            return callback(err);
          }
          if (res.status === 403) {
            // Use void to make it clear we do not want to wait this async function:
            void getInfo();
            return callback(`Auth rejected GET from ${url}`);
          }
          return callback(null, res);
        });
      } catch (err) {
        endUrl(url, statCnt);
        throw (err);
      }
      endUrl(url, statCnt);
    },
    [startUrl, endUrl, getInfo],
  );

  const superagentPost = useCallback(
    async (url, set, send, callback) => {
      if (loggingOutRef.current) {
        callback(requestSuspendedError(url));
        return;
      }

      const statCnt = startUrl(url);

      const jwt = getHomeJwt();

      try {
        const request = superagent
          .post(url)
          .set(...set)
          .withCredentials();

        if (jwt) request.auth(jwt, { type: 'bearer' });

        request.send(send)
          .end((err, res) => {
            if (err) {
              if (err.status === 403) {
                // Use void to make it clear we do not want to wait this async function:
                void getInfo();
              }
              return callback(err);
            }
            if (res.status === 403) {
              // Use void to make it clear we do not want to wait this async function:
              void getInfo();
              return callback(`Auth rejected POST to ${url}`);
            }
            return callback(null, res);
          });
      } catch (err) {
        endUrl(url, statCnt);
        throw (err);
      }
      endUrl(url, statCnt);
    },
    [startUrl, endUrl, getInfo],
  );

  const superagentGetAsync = useCallback(
    async (url, setArray = []) => {
      if (loggingOutRef.current) throw requestSuspendedError(url);

      let res;

      const jwt = getHomeJwt();

      const statCnt = startUrl(url);

      try {
        const request = superagent
          .get(url)
          .set(setArray || [])
          .withCredentials();

        if (jwt) request.auth(jwt, { type: 'bearer' });

        res = await request.send();

        if (res.status === 403) {
          logError(
            `Auth rejected GET from ${url}`,
            'AuthProvider.superagentGetAsync.rejected',
            {
              url,
              status: res?.status,
            },
          );
          // Use void to make it clear we do not want to wait this async function:
          void getInfo();
        }
      } catch (error) {
        if (error.status === 403) {
          logError(`Auth rejected GET from ${url}`);
          // Use void to make it clear we do not want to wait this async function:
          void getInfo();
        }
        endUrl(url, statCnt);
        throw (error);
      }
      endUrl(url, statCnt);
      return (res);
    },
    [startUrl, endUrl, getInfo],
  );

  /**
  * superagentPostAsync - Post to anon server, collecting access timing stats
  *
  * @param {String} url The target URL.
  * @param {Array} [setArray] A set of key/value header setting pairs.
  * @param {*} [payload] The data to send out.
  * @returns {Object} The sql transaction result
  */
  const superagentPostAsync = useCallback(
    async (url, setArray, payload, returnBlob = false) => {
      if (loggingOutRef.current) throw requestSuspendedError(url);

      let res;

      const jwt = getHomeJwt();

      const statCnt = startUrl(url);

      try {
        const request = superagent
          .post(url)
          .responseType(returnBlob ? 'blob' : 'text')
          .set(setArray || [])
          .withCredentials();

        if (jwt) request.auth(jwt, { type: 'bearer' });

        res = await request.send(payloadWithMsAgo(payload));

        if (res.status === 403) {
          logError(
            `Auth rejected POST to ${url}`,
            'AuthProvider.superagentPostAsync.rejected',
            {
              url,
              status: res?.status,
            },
          );
          // Use void to make it clear we do not want to wait this async function:
          void getInfo();
        }
      } catch (error) {
        if (error.status === 403) {
          logError(`Auth rejected POST to ${url}`);
          // Use void to make it clear we do not want to wait this async function:
          void getInfo();
        }
        endUrl(url, statCnt);
        throw (error);
      }
      endUrl(url, statCnt);
      return (res);
    },
    [startUrl, endUrl, getInfo],
  );

  //
  // This call is used to access any town server,
  // our own OR another one where we've been invited.
  // Always provide rlid if piid is provided.
  //
  const cstmrPost = useCallback(
    async (args) => {
      const {
        piid,
        rlid,
        urlPath,
        headers = ['Content-Type', 'application/json'],
        payload,
        returnBlob,
        log,
        logErrors = true,
        noRetry = false,
      } = args;

      if (!urlPath) throw new Error('cstmrPost: missing urlPath');

      if (urlPathHasMissingValue(urlPath)) {
        logError(
          `cstmrPost path carries a missing value: ${urlPath}`,
          'AuthProvider.cstmrPost.missingValueInPath',
          { urlPath },
        );
        throw new Error('cstmrPost: missing value in urlPath');
      }

      if (loggingOutRef.current) throw requestSuspendedError(urlPath);

      // A partner call needs both piid and rlid; with rlid missing the request
      // silently falls back to the home server, hitting the wrong customer.
      if ((!piidIsHome(piid)) && (!rlid)) {
        logError(
          `cstmrPost piid set without rlid, routing to home (piid=${piid}, urlPath=${urlPath})`,
          'AuthProvider.cstmrPost.piidWithoutRlid',
          { piid, urlPath },
        );
      }

      // Identify the customer server we want to reach
      const [jwt, urlBase] = await getCstmrJwtAndUrl(piid, rlid);

      // A missing origin makes a relative url, which the www server answers with index.html:
      // a failure that never looks like one
      if (!urlBase) {
        logError(
          `cstmrPost no urlBase for piid=${piid}, rlid=${rlid}, urlPath=${urlPath}`,
          'AuthProvider.cstmrPost.noUrlBase',
          { piid, urlPath },
        );
        throw new Error('cstmrPost: missing urlBase');
      }

      const url = urlBase + urlPath;

      if (log) logDev(`${log} POST at ${url}`);

      if (!jwt) logDev(`cstmrPost no JWT for piid=${piid}, rlid=${rlid}, urlPath=${urlPath}`);

      let res;
      const statCnt = startUrl(url);

      try {
        const request = superagent
          .post(url)
          .responseType(returnBlob ? 'blob' : 'text')
          .withCredentials();

        if (headers?.length) request.set(headers);
        if (jwt) request.auth(jwt, { type: 'bearer' });

        res = await request.send(payloadWithMsAgo(payload));

        // Check for stale JWT header
        // Refresh in the background if necessary
        //
        if (res?.headers?.['px-stale-jwt']) {
          logDev('JWT needs refresh!');
          // Use void to make it clear we do not want to wait this async function:
          void refreshJwt(piid, rlid);
        }

        if (res.status === 403) {
          if (logErrors) console.log(`Auth rejected POST to ${url}`);
          // Use void to make it clear we do not want to wait this async function:
          void getInfo();
        }
      } catch (error) {
        if (error.status === 403) {
          if (logErrors) console.log(`Auth rejected POST to ${url}`);

          // Check if JWT expired - refresh and retry once
          if ((!noRetry) && getHomeJwt() && (error.response?.headers?.['px-expired-jwt']
                            || error.response?.headers?.['px-no-jwt']
          )) {
            logDev('JWT expired, refreshing and retrying...');
            await refreshJwt(piid, rlid);
            endUrl(url, statCnt);
            return cstmrPost({
              ...args,
              noRetry: true,
            });
          }

          if (piidIsHome(piid) || (!rlid)) {
            // For home server, verify our auth status:
            // Use void to make it clear we do not want to wait this async function:
            void getInfo();
          }
        }
        endUrl(url, statCnt);
        // Skip browser-aborted requests (crossDomain): page unload, not a server fault.
        if ((!isUnloadingRef.current) && (!error.crossDomain)) {
          logError(
            `reject url="${url}", rlid=${rlid} piid=${piid}`,
            'AuthProvider.cstmrPost.rejected',
            {
              url,
              piid,
              err: error?.message,
              ...(error?.status && { status: error.status }),
            },
          );
        }
        throw (error);
      }

      endUrl(url, statCnt);
      return (res);
    },
    [getCstmrJwtAndUrl, startUrl, endUrl, refreshJwt, getInfo],
  );

  //
  // This call is used to access any town server,
  // our own OR another one where we've been invited.
  // Always provide rlid if piid is provided.
  //
  const cstmrGet = useCallback(
    async (args) => {
      const {
        piid,
        rlid,
        urlPath,
        headers = ['Content-Type', 'application/json'],
        returnBlob,
        log,
        logErrors = true,
        noRetry = false,
      } = args;

      if (!urlPath) throw new Error('cstmrGet: missing urlPath');

      if (urlPathHasMissingValue(urlPath)) {
        logError(
          `cstmrGet path carries a missing value: ${urlPath}`,
          'AuthProvider.cstmrGet.missingValueInPath',
          { urlPath },
        );
        throw new Error('cstmrGet: missing value in urlPath');
      }

      if (loggingOutRef.current) throw requestSuspendedError(urlPath);

      // A partner call needs both piid and rlid; with rlid missing the request
      // silently falls back to the home server, hitting the wrong customer.
      if ((!piidIsHome(piid)) && (!rlid)) {
        logError(
          `cstmrGet piid set without rlid, routing to home (piid=${piid}, urlPath=${urlPath})`,
          'AuthProvider.cstmrGet.piidWithoutRlid',
          { piid, urlPath },
        );
      }

      // Identify the customer server we want to reach
      const [jwt, urlBase] = await getCstmrJwtAndUrl(piid, rlid);

      // A missing origin makes a relative url, which the www server answers with index.html:
      // a failure that never looks like one
      if (!urlBase) {
        logError(
          `cstmrGet no urlBase for piid=${piid}, rlid=${rlid}, urlPath=${urlPath}`,
          'AuthProvider.cstmrGet.noUrlBase',
          { piid, urlPath },
        );
        throw new Error('cstmrGet: missing urlBase');
      }

      const url = urlBase + urlPath;

      if (log) logDev(`${log} GET at ${url}`);

      if (!jwt) logDev(`cstmrGet no JWT for piid=${piid}, rlid=${rlid}, urlPath=${urlPath}`);

      let res;
      const statCnt = startUrl(url);

      try {
        const request = superagent
          .get(url)
          .responseType(returnBlob ? 'blob' : 'text')
          .withCredentials();

        if (headers?.length) request.set(headers);
        if (jwt) request.auth(jwt, { type: 'bearer' });

        res = await request.send();

        // Check for stale JWT header
        // Refresh in the background if necessary
        //
        if (res?.headers?.['px-stale-jwt']) {
          logDev('JWT needs refresh!');
          // Use void to make it clear we do not want to wait this async function:
          void refreshJwt(piid, rlid);
        }

        if (res.status === 403) {
          if (logErrors) console.log(`Auth rejected GET to ${url}`);
          // Use void to make it clear we do not want to wait this async function:
          void getInfo();
        }
      } catch (error) {
        if (error.status === 403) {
          if (logErrors) console.log(`Auth rejected GET to ${url}`);

          // Check if JWT expired - refresh and retry once
          if ((!noRetry) && getHomeJwt() && (error.response?.headers?.['px-expired-jwt']
                            || error.response?.headers?.['px-no-jwt']
          )) {
            logDev('JWT expired, refreshing and retrying...');
            await refreshJwt(piid, rlid);
            endUrl(url, statCnt);
            return cstmrGet({
              ...args,
              noRetry: true,
            });
          }

          if (piidIsHome(piid) || (!rlid)) {
            // Use void to make it clear we do not want to wait this async function:
            void getInfo();
          }
        }
        endUrl(url, statCnt);
        // Skip browser-aborted requests (crossDomain): page unload, not a server fault.
        if ((!isUnloadingRef.current) && (!error.crossDomain)) {
          logError(
            `reject url="${url}", rlid=${rlid} piid=${piid}`,
            'AuthProvider.cstmrGet.rejected',
            {
              url,
              piid,
              err: error?.message,
              ...(error?.status && { status: error.status }),
            },
          );
        }
        throw (error);
      }

      endUrl(url, statCnt);
      return (res);
    },
    [getCstmrJwtAndUrl, startUrl, endUrl, refreshJwt, getInfo],
  );

  const cstmrPostBlob = useCallback(
    async (args) => {
      const {
        piid,
        rlid,
        urlPath,
        headers,
        info,
        infoFieldName = 'info',
        blob,
        blobFieldName = 'media',
        blobs,
        log,
        logErrors = true,
        noRetry = false,
      } = args;

      if (!urlPath) throw new Error('cstmrPostBlob: missing urlPath');

      if (urlPathHasMissingValue(urlPath)) {
        logError(
          `cstmrPostBlob path carries a missing value: ${urlPath}`,
          'AuthProvider.cstmrPostBlob.missingValueInPath',
          { urlPath },
        );
        throw new Error('cstmrPostBlob: missing value in urlPath');
      }

      if (loggingOutRef.current) throw requestSuspendedError(urlPath);

      // A partner call needs both piid and rlid; with rlid missing the request
      // silently falls back to the home server, hitting the wrong customer.
      if ((!piidIsHome(piid)) && (!rlid)) {
        logError(
          `cstmrPostBlob piid set without rlid, routing to home (piid=${piid}, urlPath=${urlPath})`,
          'AuthProvider.cstmrPostBlob.piidWithoutRlid',
          { piid, urlPath },
        );
      }

      // Identify the customer server we want to reach
      const [jwt, urlBase] = await getCstmrJwtAndUrl(piid, rlid);

      // A missing origin makes a relative url, which the www server answers with index.html:
      // a failure that never looks like one
      if (!urlBase) {
        logError(
          `cstmrPostBlob no urlBase for piid=${piid}, rlid=${rlid}, urlPath=${urlPath}`,
          'AuthProvider.cstmrPostBlob.noUrlBase',
          { piid, urlPath },
        );
        throw new Error('cstmrPostBlob: missing urlBase');
      }

      const url = urlBase + urlPath;

      if (log) logDev(`${log} POST blob at ${url}`);

      if (!jwt) logDev(`cstmrPostBlob no JWT for piid=${piid}, rlid=${rlid}`);

      let res;
      const statCnt = startUrl(url);

      try {
        const request = superagent
          .post(url)
          .withCredentials();

        if (headers?.length) request.set(headers);
        if (jwt) request.auth(jwt, { type: 'bearer' });

        request.field(infoFieldName, JSON.stringify(payloadWithMsAgo(info)));
        if (blobs) {
          blobs.forEach(({
            fieldName,
            blob: part,
            fileName,
          }) => request.attach(fieldName, part, fileName));
        } else {
          request.attach(blobFieldName, blob);
        }

        res = await request;

        // Check for stale JWT header
        // Refresh in the background if necessary
        //
        if (res?.headers?.['px-stale-jwt']) {
          logDev('JWT needs refresh!');
          // Use void to make it clear we do not want to wait this async function:
          void refreshJwt(piid, rlid);
        }

        if (res.status === 403) {
          if (logErrors) console.log(`Auth rejected POST to ${url}`);
          // Use void to make it clear we do not want to wait this async function:
          void getInfo();
        }
      } catch (error) {
        if (error.status === 403) {
          if (logErrors) console.log(`Auth rejected POST to ${url}`);

          // Check if JWT expired - refresh and retry once
          if ((!noRetry) && getHomeJwt() && (error.response?.headers?.['px-expired-jwt']
                            || error.response?.headers?.['px-no-jwt']
          )) {
            logDev('JWT expired, refreshing and retrying...');
            await refreshJwt(piid, rlid);
            endUrl(url, statCnt);
            return cstmrPostBlob({
              ...args,
              noRetry: true,
            });
          }

          if (piidIsHome(piid) || (!rlid)) {
            // Use void to make it clear we do not want to wait this async function:
            void getInfo();
          }
        }
        endUrl(url, statCnt);
        // Skip browser-aborted requests (crossDomain): page unload, not a server fault.
        if ((!isUnloadingRef.current) && (!error.crossDomain)) {
          logError(
            `reject url="${url}", rlid=${rlid} piid=${piid}`,
            'AuthProvider.cstmrPostBlob.rejected',
            {
              url,
              piid,
              err: error?.message,
              ...(error?.status && { status: error.status }),
            },
          );
        }
        throw (error);
      }

      endUrl(url, statCnt);
      return (res);
    },
    [getCstmrJwtAndUrl, startUrl, endUrl, refreshJwt, getInfo],
  );

  const setUserSettings = useCallback(
    async (newUserSettings) => {
      try {
        // Reflect new settings right away, before reading them back
        dispatch({
          type: 'setUserInfo',
          userInfo: {
            ...userInfo,
            settings: {
              ...userInfo?.settings,
              user: newUserSettings,
            },
          },
          partnersMapRef,
        });

        const url = config.town.back.urlPrefix
                  + config.town.back.settings.user.set;

        await superagentPostAsync(
          url,
          ['Content-Type', 'application/json'],
          newUserSettings,
        );
      } catch (err) {
        logError(
          `setUserSetting err=${err}`,
          'AuthProvider.setUserSetting.error',
          {
            err: err?.message,
            ...(err?.status && { status: err.status }),
            settingsKeys: newUserSettings && Object.keys(newUserSettings),
          },
        );
        return false;
      }

      await getInfo();

      return true;
    },
    [getInfo, superagentPostAsync, userInfo],
  );

  const addUserSetting = useCallback(
    async (addSettings) => {
      // Merge with current user-level settings
      const newUserSettings = {
        ...(pendingUserSettingsRef.current ?? userInfo?.settings?.user ?? {}),
        ...addSettings,
      };

      // Temp fix, to remove
      delete newUserSettings.tutorials;

      // Rev is managed by server:
      delete newUserSettings.rev;

      pendingUserSettingsRef.current = newUserSettings;
      userSettingsWritesRef.current += 1;

      // Reflect new settings right away, before reading them back
      dispatch({
        type: 'setUserInfo',
        userInfo: {
          ...userInfo,
          settings: {
            ...userInfo?.settings,
            user: newUserSettings,
          },
        },
        partnersMapRef,
      });

      const send = async () => {
        try {
          const url = config.town.back.urlPrefix
                    + config.town.back.settings.user.set;

          await superagentPostAsync(
            url,
            ['Content-Type', 'application/json'],
            newUserSettings,
          );
        } catch (err) {
          logError(
            `addUserSetting err=${err}`,
            'AuthProvider.addUserSetting.error',
            {
              err: err?.message,
              ...(err?.status && { status: err.status }),
              addSettingsKeys: addSettings && Object.keys(addSettings),
            },
          );
          return false;
        }

        return true;
      };

      // The route replaces the whole settings object, so writes go out one at a time.
      const sent = userSettingsChainRef.current.then(send, send);
      userSettingsChainRef.current = sent.catch(() => {});

      const ok = await sent;

      userSettingsWritesRef.current -= 1;
      if (userSettingsWritesRef.current) return ok;

      await getInfo();
      // A write started during getInfo owns the base now, so leave it alone.
      if (!userSettingsWritesRef.current) pendingUserSettingsRef.current = null;

      return ok;
    },
    [userInfo, getInfo, superagentPostAsync],
  );

  const addDeviceSetting = useCallback(
    async (addSettings) => {
      // Merge with current device-level settings
      const newDeviceSettings = {
        ...(userInfo?.settings?.device ?? {}),
        ...addSettings,
      };

      try {
        // Reflect new settings right away, before reading them back
        dispatch({
          type: 'setUserInfo',
          userInfo: {
            ...userInfo,
            settings: {
              ...userInfo?.settings,
              device: newDeviceSettings,
            },
          },
          partnersMapRef,
        });

        const url = config.town.back.urlPrefix
                  + config.town.back.settings.device.set;

        await superagentPostAsync(
          url,
          ['Content-Type', 'application/json'],
          newDeviceSettings,
        );
      } catch (err) {
        logError(
          `addDeviceSetting err=${err}`,
          'AuthProvider.addDeviceSetting.error',
          {
            err: err?.message,
            ...(err?.status && { status: err.status }),
            addSettingsKeys: addSettings && Object.keys(addSettings),
          },
        );
        return false;
      }

      await getInfo();

      return true;
    },
    [userInfo, getInfo, superagentPostAsync],
  );

  const contextValue = useMemo(
    () => ({
      auth: {
        superagentGet,
        superagentPost,
        superagentGetAsync,
        superagentPostAsync,
        cstmrPost,
        cstmrGet,
        cstmrPostBlob,
        info: userInfo,
        authStatus, // 'init' | 'loggedIn' | 'loggedOut'
        server: {
          getStatus,
          login,
          loginAdmin,
          loginPending: state.loginPending,
          loginPendingExpired: state.loginPendingExpired,
          loginStep,
          loginStepCancel,
          recoveryCodeUsed: state.recoveryCodeUsed,
          dismissRecoveryCodeUsed,
          logout,
          recheckEndedSession,
          getInfo,
          reqPending,
          serverError,
          ...serverStatus,
        },
        setUserSettings,
        addUserSetting,
        addDeviceSetting,
        getCstmrJwtAndUrl,
        partnersMap: state.partnersMap,
      },
    }),
    [
      getInfo, getStatus, login, loginAdmin, logout, recheckEndedSession, reqPending, serverError, serverStatus,
      superagentGet,
      superagentGetAsync, superagentPost, superagentPostAsync, userInfo, authStatus,
      setUserSettings, addUserSetting, addDeviceSetting, cstmrPost, cstmrGet, cstmrPostBlob,
      getCstmrJwtAndUrl, state.partnersMap, state.loginPending, state.loginPendingExpired,
      loginStep, loginStepCancel,
      state.recoveryCodeUsed, dismissRecoveryCodeUsed,
    ],
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
  invite: PropTypes.bool,
};

export default AuthProvider;
